import {
  REFERRAL_EXAMPLE_MEDIA_TYPES,
  TEMPLATE_EXAMPLES_MAX,
  type TemplateExample,
} from "@rosc/contracts";
import {
  ClipboardPaste,
  FileText,
  FileUp,
  Loader2,
  Lock,
  Upload,
  X,
} from "lucide-react";
import type { JSX } from "react";
import { useEffect, useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { exampleFromFile } from "@/components/ui/referral/ReferralFromExampleDialog";
import { ReferralSheet } from "@/components/ui/referral/sheet";
import {
  FIELD_HINT,
  FIELD_LABEL,
  FIELD_TEXTAREA,
} from "@/components/ui/referral/styles";
import { V3Button } from "@/components/ui/v3/button";
import { V3Chip } from "@/components/ui/v3/chip";
import {
  extractNoteTemplate,
  notesFilesProblem,
  type TemplateExtraction,
} from "@/lib/template-extraction";
import { cn } from "@/lib/utils";

const DOCX =
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document";
// The example letter's file types (Word files are read as text here).
const ACCEPT = [...REFERRAL_EXAMPLE_MEDIA_TYPES, DOCX, ".docx"].join(",");

type Mode = "file" | "paste";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onExtracted: (extraction: TemplateExtraction) => void;
  // The editor already has sections or instructions (they are replaced).
  replaces?: boolean;
}

// "Start from your notes" (clinical lead, 29/10/2026): the referral letter
// "from an example letter" sheet for note templates. Upload up to three notes
// (PDF, Word, photos) or paste them; patient details are removed and nothing
// is saved until the clinician reviews the template and saves it.
export const TemplateFromNotesDialog = ({
  open,
  onOpenChange,
  onExtracted,
  replaces = false,
}: Props): JSX.Element => {
  const { t } = useTranslation();
  const [mode, setMode] = useState<Mode>("file");
  const [files, setFiles] = useState<File[]>([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const abort = useRef<AbortController | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const textId = useId();

  useEffect(() => {
    if (open) {
      setFiles([]);
      setText("");
      setError(null);
      setBusy(false);
    } else abort.current?.abort();
  }, [open]);

  const addFiles = (picked: readonly File[]): void => {
    const next = [...files];
    for (const file of picked)
      if (
        !next.some(
          (other) => other.name === file.name && other.size === file.size,
        )
      )
        next.push(file);
    const problem = notesFilesProblem(next);
    setError(problem ? t(`templateFromNotes.errors.${problem}`) : null);
    setFiles(next.slice(0, TEMPLATE_EXAMPLES_MAX));
  };

  const ready =
    mode === "file"
      ? files.length > 0 && !notesFilesProblem(files)
      : text.trim().length >= 40;
  const run = async (): Promise<void> => {
    if (!ready || busy) return;
    setBusy(true);
    setError(null);
    const controller = new AbortController();
    abort.current = controller;
    try {
      const examples: TemplateExample[] =
        mode === "file"
          ? await Promise.all(files.map(exampleFromFile))
          : [{ kind: "text", text: text.trim() }];
      const extraction = await extractNoteTemplate(examples, controller.signal);
      if (controller.signal.aborted) return;
      onExtracted(extraction);
    } catch (caught) {
      if (controller.signal.aborted) return;
      const reason = caught instanceof Error ? caught.message : "";
      setError(
        reason === "too-large"
          ? t("templateFromNotes.errors.tooLarge")
          : reason === "unsupported"
            ? t("templateFromNotes.errors.unsupported")
            : reason === "unreadable" ||
                (caught as { status?: number }).status === 400
              ? t("templateFromNotes.errors.unreadable")
              : t("templateFromNotes.errors.failed"),
      );
    } finally {
      if (abort.current === controller) setBusy(false);
    }
  };

  return (
    <ReferralSheet
      description={t("templateFromNotes.description")}
      open={open}
      testId="template-from-notes"
      title={t("templateFromNotes.title")}
      onOpenChange={onOpenChange}
      onSubmit={() => {
        void run();
      }}
      footer={
        <>
          <V3Button
            className="max-sm:h-12"
            variant="secondary"
            onClick={() => {
              onOpenChange(false);
            }}
          >
            {t("common.cancel")}
          </V3Button>
          <V3Button
            className="max-sm:h-12"
            data-testid="template-from-notes-submit"
            disabled={!ready || busy}
            type="submit"
            variant="primary"
          >
            {busy && <Loader2 aria-hidden className="animate-spin" />}
            {busy
              ? t("templateFromNotes.reading")
              : t("templateFromNotes.create")}
          </V3Button>
        </>
      }
    >
      <div className="flex gap-3 rounded-xl bg-v3-surface-2 px-3 py-3 text-[13px] leading-5 text-v3-text-2 shadow-v3-hairline">
        <Lock aria-hidden className="mt-0.5 size-4 shrink-0 text-v3-text" />
        <p>
          <span className="font-semibold text-v3-text">
            {t("templateFromNotes.privacyTitle")}
          </span>{" "}
          {t("templateFromNotes.privacy")}
        </p>
      </div>
      <fieldset className="flex gap-2">
        <legend className="sr-only">{t("templateFromNotes.modeLabel")}</legend>
        <V3Chip
          aria-pressed={mode === "file"}
          className="flex-1 justify-center lg:h-9"
          onClick={() => {
            setMode("file");
            setError(null);
          }}
        >
          <FileUp aria-hidden className="size-4" />
          {t("templateFromNotes.upload")}
        </V3Chip>
        <V3Chip
          aria-pressed={mode === "paste"}
          className="flex-1 justify-center lg:h-9"
          data-testid="template-from-notes-paste-mode"
          onClick={() => {
            setMode("paste");
            setError(null);
          }}
        >
          <ClipboardPaste aria-hidden className="size-4" />
          {t("templateFromNotes.paste")}
        </V3Chip>
      </fieldset>
      {mode === "file" ? (
        <div className="space-y-2">
          <input
            ref={inputRef}
            multiple
            accept={ACCEPT}
            className="sr-only"
            data-testid="template-from-notes-file"
            tabIndex={-1}
            type="file"
            onChange={(event) => {
              addFiles(Array.from(event.target.files ?? []));
              // Picking the same file again after removing it still fires.
              event.target.value = "";
            }}
          />
          {files.length > 0 && (
            <ul className="space-y-1.5" data-testid="template-from-notes-files">
              {files.map((file) => (
                <li
                  key={`${file.name}-${file.size}`}
                  className="flex min-h-12 items-center gap-3 rounded-xl bg-v3-panel py-1.5 pl-3 pr-1.5 shadow-v3-hairline"
                >
                  <FileText
                    aria-hidden
                    className="size-4 shrink-0 text-v3-text-2"
                  />
                  <span className="min-w-0 flex-1 truncate text-[14px] font-medium text-v3-text">
                    {file.name}
                  </span>
                  <button
                    aria-label={t("templateFromNotes.remove", {
                      name: file.name,
                    })}
                    className="flex size-10 shrink-0 items-center justify-center rounded-full text-v3-text-2 transition-colors hover:bg-v3-surface-2 hover:text-v3-text focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-v3-brand-ring"
                    type="button"
                    onClick={() => {
                      const next = files.filter((other) => other !== file);
                      setFiles(next);
                      const problem = notesFilesProblem(next);
                      setError(
                        problem
                          ? t(`templateFromNotes.errors.${problem}`)
                          : null,
                      );
                    }}
                  >
                    <X aria-hidden className="size-4" />
                  </button>
                </li>
              ))}
            </ul>
          )}
          {files.length < TEMPLATE_EXAMPLES_MAX && (
            <button
              className={cn(
                "flex w-full flex-col items-center justify-center gap-2 rounded-xl border border-dashed border-v3-border-strong bg-v3-panel px-4 text-center transition-colors hover:bg-v3-surface-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-v3-brand-ring",
                files.length ? "min-h-[72px] py-3" : "min-h-[132px] py-6",
              )}
              data-testid="template-from-notes-choose"
              type="button"
              onClick={() => inputRef.current?.click()}
            >
              <Upload aria-hidden className="size-5 text-v3-text-2" />
              <span className="text-[14px] font-semibold text-v3-text">
                {files.length
                  ? t("templateFromNotes.chooseMore")
                  : t("templateFromNotes.choose")}
              </span>
              {!files.length && (
                <span className={FIELD_HINT}>
                  {t("templateFromNotes.formats")}
                </span>
              )}
            </button>
          )}
          {files.length > 0 && (
            <p className={FIELD_HINT}>{t("templateFromNotes.formats")}</p>
          )}
        </div>
      ) : (
        <div className="space-y-1.5">
          <label className={FIELD_LABEL} htmlFor={textId}>
            {t("templateFromNotes.pasteLabel")}
          </label>
          <textarea
            className={cn(FIELD_TEXTAREA, "min-h-[200px]")}
            data-testid="template-from-notes-text"
            id={textId}
            maxLength={40000}
            placeholder={t("templateFromNotes.pastePlaceholder")}
            rows={8}
            value={text}
            onChange={(event) => {
              setText(event.target.value);
              setError(null);
            }}
          />
        </div>
      )}
      {replaces && (
        <p className={FIELD_HINT} data-testid="template-from-notes-replaces">
          {t("templateFromNotes.replaces")}
        </p>
      )}
      {error && (
        <p
          className="rounded-xl bg-v3-rec-tint px-3 py-2 text-[13.5px] text-v3-rec-text"
          role="alert"
        >
          {error}
        </p>
      )}
    </ReferralSheet>
  );
};
